import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import lnwLogo from "@/assets/lnw-template-logo.png.asset.json";
import signatureAsset from "@/assets/unterschrift-tim-bauer.png.asset.json";

export interface LnwPdfItem {
  position: number;
  work_date: string; // ISO date
  hours: number;
  description: string;
}

export interface LnwPdfData {
  lnwNumber: string;
  issueDate: string; // ISO date
  customerCompany: string;
  projectName?: string | null;
  orderReference?: string | null;
  status: "draft" | "sent" | "approved" | "rejected";
  approvedAt?: string | null;
  approvedByName?: string | null;
  changeReason?: string | null;
  items: LnwPdfItem[];
}

let logoDataUrlPromise: Promise<string> | null = null;
let signatureDataUrlPromise: Promise<string> | null = null;

const loadImage = (url: string): Promise<string> =>
  fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Bild konnte nicht geladen werden: ${response.status}`);
      return response.blob();
    })
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );

const loadLogo = (): Promise<string> => {
  if (!logoDataUrlPromise) logoDataUrlPromise = loadImage(lnwLogo.url);
  return logoDataUrlPromise;
};

const loadSignature = (): Promise<string> => {
  if (!signatureDataUrlPromise) {
    signatureDataUrlPromise = loadImage(signatureAsset.url);
  }
  return signatureDataUrlPromise;
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

const fmtHours = (h: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(h);

const fmtApprovalDateTime = (iso: string) => {
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("day")}.${value("month")}.${value("year")} um ${value("hour")}:${value("minute")} Uhr`;
};

const buildLnwPdf = async (data: LnwPdfData) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const right = pageWidth - margin;
  const contentWidth = right - margin;
  const brandBlue: [number, number, number] = [0, 82, 145];
  const lightGray: [number, number, number] = [232, 232, 232];
  const rowGray: [number, number, number] = [211, 211, 211];

  const [logo, signature] = await Promise.all([
    loadLogo().catch(() => null),
    loadSignature().catch(() => null),
  ]);

  const drawPageFrame = () => {
    doc.setFillColor(...lightGray);
    doc.rect(margin, 10, contentWidth, 4.2, "F");
    if (logo) doc.addImage(logo, "PNG", right - 17.5, 11.5, 15.5, 15.9);

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(15.5);
    doc.text("FACHSPRECHPARTNER", margin, pageHeight - 20.5);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.text("IT & OT für GA, FM und Gebäudebetreiber", margin, pageHeight - 16.2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("E-Mail:  info@fachsprechpartner.de", right, pageHeight - 20.5, { align: "right" });
  };

  drawPageFrame();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text("Leistungsnachweis", margin, 27);

  const headerRows: [string, string][] = [
    ["Auftraggeber", data.customerCompany],
    ["Projekt", data.projectName || "–"],
    ["Auftragskennung", data.orderReference || "–"],
    ["LNW-Kennung", data.lnwNumber],
    ["Datum", fmtDate(data.issueDate)],
  ];

  let y = 47.5;
  doc.setFontSize(9.5);
  headerRows.forEach(([label, value]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(label, margin, y);
    const valueLines = doc.splitTextToSize(value, 91) as string[];
    valueLines.forEach((line: string, i: number) => {
      doc.text(line, right, y + i * 4.3, { align: "right" });
    });
    const rowBottom = y + (valueLines.length - 1) * 4.3 + 1.3;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.15);
    doc.line(margin + 48, rowBottom, right, rowBottom);
    y = rowBottom + 4.8;
  });

  const totalHours = data.items.reduce((acc, i) => acc + Number(i.hours), 0);

  autoTable(doc, {
    startY: y + 11,
    margin: { left: margin, right: margin },
    head: [["Pos.", "Datum", "Aufwand (h)", "Beschreibung"]],
    body: data.items.map((item) => [
      String(item.position),
      fmtDate(item.work_date),
      fmtHours(Number(item.hours)),
      item.description,
    ]),
    foot: [["Gesamtaufwand", "", "", `${fmtHours(totalHours)} h`]],
    theme: "plain",
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "normal",
      lineColor: [90, 90, 90],
      lineWidth: { bottom: 0.25 },
      fontSize: 9.5,
      cellPadding: { top: 0.8, right: 1.5, bottom: 0.8, left: 1.5 },
    },
    bodyStyles: {
      fillColor: rowGray,
      textColor: [0, 0, 0],
      lineColor: [140, 140, 140],
      lineWidth: 0.12,
      fontSize: 9.5,
      cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
      valign: "middle",
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: [120, 120, 120],
      lineWidth: { top: 0.18, bottom: 0.18 },
      fontSize: 9.5,
      cellPadding: { top: 3.2, right: 1.5, bottom: 3.2, left: 1.5 },
    },
    columnStyles: {
      0: { cellWidth: 10.5, halign: "center" },
      1: { cellWidth: 23 },
      2: { cellWidth: 27, halign: "center" },
      3: { cellWidth: "auto" },
    },
    didParseCell: (hookData) => {
      if (hookData.section === "foot") {
        if (hookData.column.index === 0) hookData.cell.colSpan = 3;
        if (hookData.column.index === 3) hookData.cell.styles.halign = "right";
      }
    },
    didDrawPage: (hookData) => {
      if (hookData.pageNumber > 1) drawPageFrame();
    },
    pageBreak: "auto",
    rowPageBreak: "avoid",
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  let contentY = tableEndY + 10;
  if (contentY > pageHeight - 78) {
    doc.addPage();
    drawPageFrame();
    contentY = 34;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text("Die Leistungen wurden wie oben beschrieben erbracht.", margin + 2, contentY);

  let approvalY = contentY + 8;
  doc.setTextColor(...brandBlue);
  if (data.status === "approved" && data.approvedAt) {
    doc.setFont("helvetica", "italic");
    const approval = `Ohne Unterschrift gültig, online am ${fmtApprovalDateTime(data.approvedAt)} durch ${data.approvedByName ?? "Auftraggeber"} genehmigt`;
    doc.text(doc.splitTextToSize(approval, contentWidth - 4), margin + 2, approvalY);
  } else {
    doc.setFont("helvetica", "italic");
    doc.text("Ohne Unterschrift gültig, Genehmigung ausstehend", margin + 2, approvalY);
  }

  if (data.changeReason) {
    approvalY += 5;
    doc.setFontSize(8);
    doc.text(
      doc.splitTextToSize(`Nachträgliche Änderung: ${data.changeReason}`, contentWidth - 4),
      margin + 2,
      approvalY,
    );
    doc.setFontSize(9.5);
  }

  const customerLineY = approvalY + 7;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.line(margin + 2, customerLineY, margin + 86, customerLineY);
  doc.line(margin + 86, customerLineY - 5, margin + 86, customerLineY + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text("Unterschrift Auftraggeber", margin + 2, customerLineY + 4);
  doc.setFontSize(7);
  doc.text("(Ort, Datum)", margin + 47, customerLineY + 4);
  doc.setTextColor(0, 0, 0);

  const contractorLineY = customerLineY + 24;
  if (signature) doc.addImage(signature, "PNG", margin + 7, contractorLineY - 13.5, 43, 14.1);
  doc.setDrawColor(0, 0, 0);
  doc.line(margin + 2, contractorLineY, margin + 86, contractorLineY);
  doc.line(margin + 86, contractorLineY - 5, margin + 86, contractorLineY + 5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...brandBlue);
  doc.text("Tim Bauer", margin + 64, contractorLineY - 2.2);
  doc.text(`Emmendingen, ${fmtDate(data.issueDate)}`, margin + 64, contractorLineY + 3.3);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9.5);
  doc.text("Unterschrift Auftragnehmer", margin + 2, contractorLineY + 4);
  doc.setFontSize(7);
  doc.text("(Ort, Datum)", margin + 45, contractorLineY + 4);
  doc.setTextColor(0, 0, 0);

  return doc;
};

export const generateLnwPdf = async (data: LnwPdfData): Promise<void> => {
  const doc = await buildLnwPdf(data);
  doc.save(`Leistungsnachweis_${data.lnwNumber}.pdf`);
};

/** Base64 (without data URL prefix) of the generated PDF, for archiving. */
export const generateLnwPdfBase64 = async (data: LnwPdfData): Promise<string> => {
  const doc = await buildLnwPdf(data);
  const dataUri = doc.output("datauristring");
  return dataUri.split(",").pop() ?? "";
};
